import React from 'react';
import {Route, Switch, useHistory, useLocation} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {get} from 'lodash';
import {PATHS} from 'Routes';
import {SelectOptionType} from '../../utils/types';
import {useIdentityVerification} from './useIdentityVerification';
import {useComputedReservationDetails} from '../../context/computedReservationDetails';
import styles from './VerifyModalScreen.module.sass';
import Modal from 'components/acommon/Modal';
import Tabs, {TabsItem} from 'components/acommon/Tabs';
import Select from 'components/acommon/FieldSelect';
import SelfieIdentity from 'views/VerifyModalScreen/SelfieIdentity';
import ConfirmIdentityResultScreen from 'views/ConfirmIdentityResultScreen';
import FrontSideDocumentIdentity from './FrontSideDocumentIdentity';
import BackSideDocumentIdentity from './BackSideDocumentIdentity';
import {
  getIdentityTypeItems,
  IdentityVerificationMethod,
} from 'views/ConfirmIdentityGuideScreen/ConfirmIdentityGuideScreen';

export enum LocationStates {
  frontSideDocumentPhoto = 'frontSideDocumentPhoto',
  backSideDocumentPhoto = 'backSideDocumentPhoto',
  selfiePhoto = 'selfiePhoto',
  identityVerificationState = 'identityVerificationState',
}

export type IdentityLocationStateType = {
  [LocationStates.frontSideDocumentPhoto]?: PhotoLocationStateType;
  [LocationStates.backSideDocumentPhoto]?: PhotoLocationStateType;
  [LocationStates.selfiePhoto]?: PhotoLocationStateType;
  [LocationStates.identityVerificationState]?: IdentityVerificationLocationStateType;
};

export type PhotoLocationStateType = {
  checkId: string;
  picture: string;
  failedOCRAttempts?: number;
  status?: string;
  isRetryBiomatch?: boolean;
};

export type IdentityVerificationLocationStateType = {
  isIdentityVerificationFinished?: boolean;
  documentsPassed?: boolean;
  biomatchPassed?: boolean;
};

export type VerifyLocationStateType = {
  documentType: SelectOptionType;
  identityType: IdentityVerificationMethod;
} | null;

export type VerifyPathname =
  | PATHS.verificationDocumentFront
  | PATHS.verificationDocumentBack
  | PATHS.verificationSelfie
  | PATHS.verificationResult;

function VerifyModalScreen() {
  const history = useHistory();
  const {t} = useTranslation();
  const location = useLocation<VerifyLocationStateType>();
  const documentType = get(location, 'state.documentType.value');
  const nationality = get(location, 'state.formData.nationality.value');
  const {hasBiomatch} = useComputedReservationDetails();
  const {isDocTwoSides} = useIdentityVerification({nationality, documentType});

  const tabsItems: TabsItem[] = React.useMemo(() => {
    const items = [];

    items.push({
      label: t('front'),
      path: {...location, pathname: PATHS.verificationDocumentFront},
    });

    if (isDocTwoSides) {
      items.push({
        label: t('back'),
        path: {...location, pathname: PATHS.verificationDocumentBack},
      });
    }

    if (hasBiomatch) {
      items.push({
        label: t('selfie'),
        path: {...location, pathname: PATHS.verificationSelfie},
      });
    }

    return items;
  }, [isDocTwoSides, t, location, hasBiomatch]);

  const isVisible = new RegExp(`${PATHS.verificationStart}/\\w+$`).test(
    location.pathname,
  );

  const handleDocumentUploadMethodChange = ({
    value: newMethod,
  }: {
    value: IdentityVerificationMethod;
  }) => {
    if (newMethod !== location.state?.identityType) {
      const newState = {...location.state, identityType: newMethod};
      history.replace(location.pathname, newState);
    }
  };

  const optionsConfig = {
    isUploadDisabled: location.pathname === PATHS.verificationSelfie,
  };

  const options = getIdentityTypeItems(
    handleDocumentUploadMethodChange,
    t,
    optionsConfig,
  );
  const defaultOption = options.find(({value}) => location.state?.identityType === value);
  return (
    <Modal
      visible={isVisible}
      onClose={() => history.push(PATHS.verificationStart, location.state)}
      warningCloseModal
    >
      <div className={styles.header}>
        <div className={styles.title}>{location.state?.documentType?.label || ''}</div>
        <div className={styles.headerSelect}>
          <Select
            defaultValue={defaultOption}
            options={options}
            onChange={handleDocumentUploadMethodChange}
          />
        </div>
      </div>
      <Tabs items={tabsItems}>
        <Switch>
          <Route path={PATHS.verificationDocumentFront}>
            <FrontSideDocumentIdentity
              documentType={documentType}
              nationality={nationality}
            />
          </Route>
          <Route path={PATHS.verificationDocumentBack}>
            <BackSideDocumentIdentity
              documentType={documentType}
              nationality={nationality}
            />
          </Route>
          <Route path={PATHS.verificationSelfie}>
            <SelfieIdentity />
          </Route>
          <Route path={PATHS.verificationResult}>
            <ConfirmIdentityResultScreen />
          </Route>
        </Switch>
      </Tabs>
    </Modal>
  );
}

export {VerifyModalScreen};
